<template>
  <div class="dashboard-page">
    <!-- Header -->
    <div class="dashboard-header">
      <h1 class="dashboard-title">Analisi</h1>
      <div class="header-buttons">
        <button class="compare-button" @click="openCompareModal">
          Confronta Analisi
        </button>
        <button class="create-button" @click="openCreateModal">
          Nuova Analisi
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading && analyses.length === 0" class="loading-container">
      <img src="@/assets/Icons/Loading.gif" alt="Loading" class="loading-gif" />
      <p class="loading-text">Caricamento analisi...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && analyses.length === 0" class="empty-state">
      <h2 class="empty-title">Nessuna analisi trovata</h2>
      <p class="empty-text">Crea la tua prima analisi per iniziare!</p>
      <button class="create-button-large" @click="openCreateModal">
        Crea Nuova Analisi
      </button>
    </div>

    <!-- Analyses List -->
    <div v-else class="analyses-container">
      <div class="analyses-list">
        <AnalysisCard
          v-for="analysis in analyses"
          :key="analysis.analysis_id"
          :analysis="analysis"
        />
      </div>

      <!-- PaginationPage -->
      <PaginationPage
        v-if="totalPages > 1"
        :current-page="currentPage"
        :total-pages="totalPages"
        :total-items="totalAnalyses"
        :items-per-page="limit"
        @page-change="handlePageChange"
      />
    </div>

    <!-- Create Analysis Modal -->
    <CreateAnalysisModal
      :is-open="showCreateModal"
      @close="closeCreateModal"
      @create="handleCreateAnalysis"
    />

    <!-- Multi-Analysis Comparison Modal -->
    <MultiAnalysisModal
      :is-open="showCompareModal"
      @close="closeCompareModal"
      @compare="handleCompare"
    />

    <!-- Toast Notifications -->
    <ToastNotification
      v-if="toastMessage"
      :message="toastMessage"
      :type="toastType"
      :show="showToast"
      @close="hideToast"
    />
  </div>
</template>

<script>
import { useAnalysisStore } from '@/stores/analysisStore';
import { useAuthStore } from '@/stores/authStore';
import AnalysisCard from './AnalysisCard.vue';
import CreateAnalysisModal from './CreateAnalysisModal.vue';
import MultiAnalysisModal from './MultiAnalysisModal.vue';
import PaginationPage from '@/components/widgets/PaginationPage.vue';
import ToastNotification from '@/components/widgets/ToastNotification.vue';

export default {
  name: 'AnalisiDashboard',
  components: {
    AnalysisCard,
    CreateAnalysisModal,
    MultiAnalysisModal,
    PaginationPage,
    ToastNotification,
  },
  setup() {
    const analysisStore = useAnalysisStore();
    return { analysisStore };
  },
  data() {
    return {
      showCreateModal: false,
      showCompareModal: false,
      userId: null,
      toastMessage: '',
      toastType: 'info',
      showToast: false,
      pollingAnalyses: new Set(),
    };
  },
  computed: {
    loading() {
      return this.analysisStore.loading;
    },
    analyses() {
      return this.analysisStore.analyses;
    },
    totalAnalyses() {
      return this.analysisStore.totalAnalyses;
    },
    currentPage() {
      return this.analysisStore.currentPage;
    },
    totalPages() {
      return this.analysisStore.totalPages;
    },
    limit() {
      return this.analysisStore.limit;
    },
    activeAnalyses() {
      return this.analysisStore.activeAnalyses;
    },
  },
  async mounted() {
    // Get user ID
    const authStore = useAuthStore();
    const user = authStore.user;
    if (!user) {
      this.$router.push('/login');
      return;
    }

    this.userId = user.id;

    // Fetch analyses
    await this.fetchAnalyses();

    // Start polling for active analyses
    this.startPollingForActiveAnalyses();

    // Request notification permission
    this.requestNotificationPermission();
  },
  beforeUnmount() {
    // Stop all polling
    this.analysisStore.stopAllPolling();
  },
  methods: {
    async fetchAnalyses() {
      try {
        const offset = (this.currentPage - 1) * this.limit;
        await this.analysisStore.fetchUserAnalyses(this.userId, {
          limit: this.limit,
          offset: offset,
        });
      } catch (error) {
        console.error('Error fetching analyses:', error);
        this.showToastMessage('Errore nel caricamento delle analisi', 'error');
      }
    },

    openCreateModal() {
      this.showCreateModal = true;
    },

    closeCreateModal() {
      this.showCreateModal = false;
    },

    openCompareModal() {
      this.showCompareModal = true;
    },

    closeCompareModal() {
      this.showCompareModal = false;
    },

    handleCompare(analysisIds) {
      // Navigate to comparison page with selected IDs
      this.$router.push({
        name: 'MultiAnalysisComparison',
        query: { ids: analysisIds.join(',') },
      });
      this.closeCompareModal();
    },

    async handleCreateAnalysis(analysisData) {
      try {
        const result = await this.analysisStore.createAnalysis(analysisData);
        
        this.showToastMessage(
          'Analisi creata con successo! Elaborazione in corso...',
          'success'
        );

        // Start polling for the new analysis
        this.analysisStore.startPolling(
          result.analysis_id,
          this.userId,
          this.onAnalysisComplete
        );
        this.pollingAnalyses.add(result.analysis_id);
      } catch (error) {
        console.error('Error creating analysis:', error);
        this.showToastMessage(
          error.message || 'Errore nella creazione dell\'analisi',
          'error'
        );
      }
    },

    async handlePageChange(page) {
      this.analysisStore.setCurrentPage(page);
      await this.fetchAnalyses();
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Restart polling for active analyses on new page
      this.startPollingForActiveAnalyses();
    },

    startPollingForActiveAnalyses() {
      // Stop existing polling
      this.pollingAnalyses.forEach((analysisId) => {
        this.analysisStore.stopPolling(analysisId);
      });
      this.pollingAnalyses.clear();

      // Start polling for all active analyses
      this.activeAnalyses.forEach((analysis) => {
        if (!this.pollingAnalyses.has(analysis.analysis_id)) {
          this.analysisStore.startPolling(
            analysis.analysis_id,
            this.userId,
            this.onAnalysisComplete
          );
          this.pollingAnalyses.add(analysis.analysis_id);
        }
      });
    },

    onAnalysisComplete(result) {
      // Remove from polling set
      this.pollingAnalyses.delete(result.analysis_id);

      // Show notification based on status
      if (result.status === 'completed') {
        const analysis = this.analyses.find(
          (a) => a.analysis_id === result.analysis_id
        );
        const companyName = analysis ? analysis.company_name : 'l\'azienda';
        
        this.showToastMessage(
          `Analisi completata per ${companyName}!`,
          'success'
        );

        // Show browser notification
        this.showBrowserNotification(
          'Analisi Completata!',
          `L'analisi per ${companyName} è pronta.`
        );
      } else if (result.status === 'failed') {
        const analysis = this.analyses.find(
          (a) => a.analysis_id === result.analysis_id
        );
        const companyName = analysis ? analysis.company_name : 'l\'azienda';
        
        this.showToastMessage(
          `Analisi fallita per ${companyName}. Riprova più tardi.`,
          'error'
        );
      }
    },

    showToastMessage(message, type = 'info') {
      this.toastMessage = message;
      this.toastType = type;
      this.showToast = true;
    },

    hideToast() {
      this.showToast = false;
      setTimeout(() => {
        this.toastMessage = '';
      }, 300);
    },

    requestNotificationPermission() {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    },

    showBrowserNotification(title, body) {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(title, {
          body: body,
          icon: '/favicon.ico',
        });
      }
    },
  },
};
</script>

<style scoped>
.dashboard-page {
  position: relative;
  min-height: 100vh;
  padding: 2rem;
  padding-top: 120px; /* Account for fixed navbar */
  background: linear-gradient(135deg, #ffffff, #56bcee);
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto 2rem;
  gap: 1rem;
}

.dashboard-title {
  font-size: 2.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.header-buttons {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.create-button,
.compare-button {
  background: #2e4eed;
  border: 2px solid #2e4eed;
  color: white;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.create-button:hover,
.compare-button:hover {
  background: #1e3ed8;
  border-color: #1e3ed8;
}

.compare-button {
  background: white;
  color: #2e4eed;
}

.compare-button:hover {
  background: rgba(46, 78, 237, 0.1);
  border-color: #1e3ed8;
  color: #1e3ed8;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1rem;
}

.loading-gif {
  width: 60px;
  height: 60px;
}

.loading-text {
  font-size: 1.125rem;
  color: #1a1a1a;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  gap: 1.5rem;
  max-width: 600px;
  margin: 0 auto;
}

.empty-title {
  font-size: 2rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.empty-text {
  font-size: 1.125rem;
  color: #6b7280;
  margin: 0;
}

.create-button-large {
  background: #2e4eed;
  border: 2px solid #2e4eed;
  color: white;
  padding: 1rem 2rem;
  font-size: 1.125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.create-button-large:hover {
  background: #1e3ed8;
  border-color: #1e3ed8;
}

.analyses-container {
  max-width: 1200px;
  margin: 0 auto;
}

.analyses-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
}

@media (max-width: 768px) {
  .dashboard-page {
    padding: 1rem;
    padding-top: 100px; /* Account for fixed navbar on mobile */
  }

  .dashboard-header {
    flex-direction: column;
    align-items: stretch;
  }

  .dashboard-title {
    font-size: 2rem;
    text-align: center;
  }

  .header-buttons {
    flex-direction: column;
    width: 100%;
  }

  .create-button,
  .compare-button {
    width: 100%;
  }

  .empty-title {
    font-size: 1.5rem;
  }

  .empty-text {
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .dashboard-title {
    font-size: 1.75rem;
  }

  .empty-title {
    font-size: 1.25rem;
  }
}
</style>
